'use client';

import { useEffect } from 'react';
import { Check, X } from 'lucide-react';
import { AnimatePresence, motion } from 'framer-motion';
import { StripeEmbeddedCheckout } from '@/components/checkout/StripeEmbeddedCheckout';

interface ReportCheckoutModalProps {
  clientSecret: string;
  priceUsd: number;
  onClose: () => void;
  refreshClientSecret: () => Promise<string>;
}

/** Stripe embedded checkout for a paid report, shown over the current page. */
export function ReportCheckoutModal({
  clientSecret,
  priceUsd,
  onClose,
  refreshClientSecret,
}: ReportCheckoutModalProps) {
  const isOpen = clientSecret !== '';

  useEffect(() => {
    if (!isOpen) return;
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleEscape);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = '';
    };
  }, [isOpen, onClose]);

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
          onClick={onClose}
        >
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            transition={{ type: 'spring', duration: 0.3 }}
            className="relative w-full max-w-2xl bg-white rounded-lg shadow-xl overflow-hidden"
            onClick={(event) => event.stopPropagation()}
          >
            <button
              onClick={onClose}
              aria-label="Close checkout"
              className="absolute top-4 right-4 p-2 bg-white hover:bg-gray-100 transition-colors z-10"
            >
              <X className="w-5 h-5 text-gray-700" />
            </button>

            <div className="p-6 pt-14 max-h-[90vh] overflow-y-auto">
              <StripeEmbeddedCheckout
                clientSecret={clientSecret}
                refreshClientSecret={refreshClientSecret}
              />

              <div className="border-t mt-6 pt-4 space-y-3">
                <p className="text-xs text-gray-600 text-center">
                  A single charge of ${priceUsd}. Nothing recurring.
                </p>
                <div className="flex items-center justify-center gap-2 text-sm text-gray-600">
                  <Check className="w-4 h-4 text-[#2A6354]" />
                  <span>Pay safe &amp; secure</span>
                  <span className="text-gray-400">•</span>
                  <span>Powered by Stripe</span>
                </div>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
